import { Alert, Box, Snackbar } from '@mui/material';
import { getInterviewApiUrl } from '@utils/environment';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useInterviewCompletion } from '../../hooks/use-interview-completion';
import { useInterviewHooks } from '../../hooks/use-interview-hooks';
import ControlPanel from './components/control-panel';
import {
  InterviewCompleting,
  InterviewErrorLoading,
  InterviewNetworkConnectionLost,
  InterviewResumeError,
  InterviewZaraBusy,
} from './components/interview-state-views';
import { useAudioFlow } from './hooks/use-audio-flow';
import useAudioRecorder from './hooks/use-audio-recorder';
import useAvService from './hooks/use-av-service';
import useChatClient from './hooks/use-chat-client';
import { useClosePageAlert } from './hooks/use-close-page-alert';
import useTimeLeft from './hooks/use-time-left';
import useVideoUploadClient from './hooks/use-video-upload-client';
import { InteractionState, NetworkState } from './types';

type InterviewMainProps = {
  interviewId: string;
  accessToken: string;
  requestedProvider?: 'aurora' | 'polaris' | 'zenith';
  onStarted: () => void;
  onCompleted: () => void;
  updateAssessmentCache: (startedAt: Date) => void;
};

const InterviewMain = ({
  interviewId,
  accessToken,
  requestedProvider,
  onStarted,
  onCompleted,
  updateAssessmentCache,
}: InterviewMainProps) => {
  const audioFlow = useAudioFlow(interviewId);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [interactionState, setInteractionState] =
    useState<InteractionState>('idle');
  const [networkState, setNetworkState] = useState<NetworkState>('stable');
  const [shouldStartCompleting, setShouldStartCompleting] =
    useState<boolean>(false);
  const [isCompleting, setIsCompleting] = useState<boolean>(false);
  const [isComplete, setIsComplete] = useState<boolean>(false);
  const [showConnectionRecoveredMessage, setShowConnectionRecoveredMessage] =
    useState<boolean>(false);
  const [zaraBusyError, setZaraBusyError] = useState<boolean>(false);
  const [resumeError, setResumeError] = useState<boolean>(false);

  // Ref indirection so callbacks above can reach sendPlaybackComplete from
  // useChatClient below without a hook-order dependency.
  const sendPlaybackCompleteRef = useRef<() => void>(() => {});
  const sendPlaybackCompleteStable = useCallback(() => {
    sendPlaybackCompleteRef.current();
  }, []);

  const {
    play,
    interrupt,
    startRecording,
    stopRecording,
    videoStream,
    isPlaying,
    analyserNode,
  } = useAvService({
    audioFlow,
    onVideoChunk: (chunk) => queueChunk(chunk),
    onPlaybackComplete: () => {
      sendPlaybackCompleteRef.current();
      if (interactionState !== 'recording') {
        setInteractionState('idle');
      }
    },
  });

  const {
    onResponseAudio,
    onResponseInterrupted,
    onConnectionUnstable,
    onConnectionLost,
    onConnectionRecovered,
    onChatClientError,
    onChatClientClosed,
    onChatClientComplete,
    onChatClientCompleted,
  } = useInterviewHooks({
    interviewId,
    setInteractionState,
    onCompleted,
    play,
    interrupt,
    sendPlaybackComplete: sendPlaybackCompleteStable,
    stopRecording,
    setNetworkState,
    setShowConnectionRecoveredMessage,
    setZaraBusyError,
    setResumeError,
    setShouldStartCompleting,
    setIsComplete,
  });

  const {
    isStarted: isInterviewStarted,
    videoUploadUrl,
    startedAt,
    duration,
    inputSampleRate,
    appendInputAudio,
    completeInterview,
    sendPlaybackComplete,
  } = useChatClient({
    audioFlow,
    apiUrl: getInterviewApiUrl() || 'missing-interview-api-url',
    interviewId,
    accessToken,
    requestedProvider,
    onResponseAudio,
    onResponseInterrupted,
    onConnectionUnstable,
    onConnectionLost,
    onConnectionRecovered,
    onClosed: onChatClientClosed,
    onError: onChatClientError,
    onStarted,
    onComplete: onChatClientComplete,
    onCompleted: onChatClientCompleted,
    updateAssessmentCache,
  });

  useEffect(() => {
    sendPlaybackCompleteRef.current = sendPlaybackComplete;
  }, [sendPlaybackComplete]);

  useEffect(() => {
    if (isInterviewStarted) {
      startRecording();
      setInteractionState('recording');
    } else {
      setInteractionState('idle');
    }
  }, [isInterviewStarted, startRecording]);

  useAudioRecorder({
    audioFlow,
    isRecording: isInterviewStarted && networkState !== 'lost',
    onAudioChunk: appendInputAudio,
    inputSampleRate,
  });

  const { queueChunk, finishUpload: finishVideoUpload } = useVideoUploadClient({
    videoUploadUrl,
    isUploading: isInterviewStarted,
  });

  const timeLeft = useTimeLeft({
    startedAt,
    duration,
  });

  const currentVideoRef = videoRef.current;
  useEffect(() => {
    if (currentVideoRef) {
      currentVideoRef.srcObject = videoStream;
    }
  }, [videoStream, currentVideoRef]);

  const { cleanupAndCompleteInterview, forceCompleteInterview } =
    useInterviewCompletion({
      setIsCompleting,
      stopRecording,
      finishUserCameraUpload: finishVideoUpload,
      stopScreenRecording: undefined,
      finishScreenRecordingUpload: undefined,
      completeInterview,
    });

  useEffect(() => {
    // Wait for the AI to finish talking before completing the interview.
    const readyToFinish = interactionState !== 'aiSpeaking' && !isPlaying;
    if (shouldStartCompleting && readyToFinish) {
      cleanupAndCompleteInterview();
    }
  }, [
    interactionState,
    isPlaying,
    shouldStartCompleting,
    cleanupAndCompleteInterview,
  ]);

  useClosePageAlert({
    showAlert: !isComplete,
  });

  if (zaraBusyError) {
    return <InterviewZaraBusy />;
  }

  if (resumeError) {
    return <InterviewResumeError />;
  }

  if (networkState === 'lost') {
    return <InterviewNetworkConnectionLost />;
  }

  if (interactionState === 'error') {
    return <InterviewErrorLoading />;
  }

  if (isCompleting) {
    return <InterviewCompleting />;
  }

  return (
    <Box
      width='100%'
      height='100vh'
      display='flex'
      flexDirection='column'
      position='relative'
      sx={{ backgroundColor: '#262626' }}
    >
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        open={networkState === 'unstable'}
      >
        <Alert severity='warning'>
          Unstable network connection, attempting to reconnect...
        </Alert>
      </Snackbar>
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        open={showConnectionRecoveredMessage}
        autoHideDuration={3000}
      >
        <Alert severity='success'>Connection recovered</Alert>
      </Snackbar>
      <Box flexGrow={1} position='relative'>
        {/* biome-ignore lint/a11y/useMediaCaption: local camera preview without captions */}
        <video
          ref={videoRef}
          autoPlay
          playsInline
          style={{
            objectFit: 'contain',
            width: '100%',
            height: '100%',
            position: 'absolute',
          }}
        />
        <ControlPanel
          interactionState={interactionState}
          avServiceAnalyser={analyserNode}
          assessmentId={interviewId}
          timeLeft={timeLeft}
          onUserEndedInterview={forceCompleteInterview}
          isLoading={!isInterviewStarted}
        />
      </Box>
    </Box>
  );
};

export default InterviewMain;
